import React, { useEffect, useState, useCallback, useMemo } from "react";
import { userDetailsAPI, deleteUserAPI, deleteMultiUserAPI, exportToExcelFile } from "../../../api/userAPI";
import { useMsal } from "@azure/msal-react";
import styles from './user.module.css';
import { ListItem, CircularProgress, Box } from "@mui/material";
import ExportLoader from "../../../components/snackbar/exportLoader";
import resetBtn from '../../../../assets/images/resetBtn.png';
import plusIcon from "../../../../assets/images/plusIcon.svg";
import adminDeleteIcon from "../../../../assets/images/deleteIcon.svg";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import CustomButton from "Samarth Module/components/buttons/CustomButton";
import UserTable from "./userTable";
import DeletePopup from "./deletePopup";
import Excel from "../../../../assets/images/Excel.svg";
import AddAndEditPopup from "./addAndEditPopup";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import { removeUserFromAdGroup, removeMultipleUsersFromAdGroup } from "../../../api/azureADGroupAPI";

const User = () => {
    const adminType = 'User Access';
    const { accounts, instance } = useMsal();
    const activeAccount = accounts[0];

    const [userDetails, setUserDetails] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [openAddUser, setOpenAddUser] = useState(false);
    const [bulkDeletePopupOpen, setBulkDeletePopupOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [actionType, setActionType] = useState('add');
    const [exportLoader, setExportLoader] = useState(false)

    const [snackBarObject, setSnackBarObject] = useState({
        type: 'success',
        message: '',
        snackbarOpen: false
    });

    // SnackBar helper functions
    const showSnackbar = useCallback((type, message) => {
        setSnackBarObject({
            type,
            message,
            snackbarOpen: true
        });
    }, []);

    const closeSnackbar = useCallback(() => {
        setSnackBarObject(prev => ({
            ...prev,
            snackbarOpen: false
        }));
    }, []);

    // Fetch user details from API
    const fetchUserDetails = useCallback(async () => {
        if (!activeAccount) return;

        setIsLoading(true);
        const payload = {
            email: activeAccount.username,
            type: adminType,
            role: activeAccount?.idTokenClaims?.roles,
        };

        try {
            const users = await userDetailsAPI(payload);
            setUserDetails(users);
        } catch (error) {
            console.error("Error fetching user details:", error);
            showSnackbar('error', 'Failed to fetch user details. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [activeAccount, adminType, showSnackbar]);

    const LoadingComponent = () => (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
            flexDirection="column"
            gap={2}
        >
            <CircularProgress size={60} />
            <div style={{ fontSize: '16px', color: '#666' }}>
                Loading user data...
            </div>
        </Box>
    );

    const parseScopeValue = useCallback((scopeValue) => {
        if (Array.isArray(scopeValue)) {
            return scopeValue
                .map(value => (value === null || value === undefined ? '' : String(value).trim()))
                .filter(Boolean);
        }

        if (scopeValue === null || scopeValue === undefined || scopeValue === '') {
            return [];
        }

        if (typeof scopeValue === 'string') {
            return scopeValue
                .split(',')
                .map(value => value.replaceAll('[', '').replaceAll(']', '').replace(/['"`]/g, '').trim())
                .filter(Boolean);
        }

        return [String(scopeValue).trim()].filter(Boolean);
    }, []);

    const getUserValueForFilter = useCallback((user, filterLabel) => {
        switch (filterLabel) {
            case 'Name':
                return user.Name;
            case 'Persona':
                return user.Persona;
            case 'Email ID':
                return user['Email ID'];
            case 'Access type':
                return user['Access Type'];
            case 'Scope':
                return user.Scope;
            case 'Small C':
            case 'Site code':
            case 'Site cluster':
            case 'BG':
                return parseScopeValue(user['Scope Value']);
            case 'User type':
                return user['User Type'];
            default:
                return null;
        }
    }, [parseScopeValue]);

    const doesUserMatchFilter = useCallback((user, filter) => {
        if (!filter?.selectedData?.length) {
            return true;
        }

        const userValue = getUserValueForFilter(user, filter.label);

        if (Array.isArray(userValue)) {
            return userValue.some(value => filter.selectedData.includes(value));
        }

        return filter.selectedData.includes(userValue);
    }, [getUserValueForFilter]);

    const getFilteredUsers = useCallback(() => {
        if (!userDetails?.users || !userDetails?.filters) {
            return [];
        }

        return userDetails.users.filter(user =>
            userDetails.filters.every(filter => doesUserMatchFilter(user, filter))
        );
    }, [userDetails, doesUserMatchFilter]);

    const normalizeFilterLabel = useCallback((value) => {
        if (value === null || value === undefined) {
            return '';
        }

        return String(value).trim().toLowerCase();
    }, []);

    const getComparableOptionValue = useCallback((option) => {
        if (option && typeof option === 'object') {
            return option.value ?? option.label ?? option.name ?? option.id ?? null;
        }

        return option;
    }, []);

    const getFilterRawOptions = useCallback((targetFilter) => {
        if (!targetFilter) {
            return [];
        }

        const targetLabel = normalizeFilterLabel(targetFilter.label);
        const { data } = targetFilter;

        if (Array.isArray(data)) {
            const matchingNestedOptions = data.find(item => {
                if (!item || typeof item !== 'object' || !Array.isArray(item.data)) {
                    return false;
                }

                const candidateLabels = [
                    item.label,
                    item.key,
                    item.name,
                    item.filter,
                    item.filterLabel,
                    item.type
                ].map(normalizeFilterLabel);

                return candidateLabels.includes(targetLabel);
            });

            return Array.isArray(matchingNestedOptions?.data)
                ? matchingNestedOptions.data
                : data;
        }

        if (data && typeof data === 'object') {
            if (Array.isArray(data.data)) {
                return data.data;
            }

            const directMatch = Object.entries(data).find(([key, value]) => {
                if (normalizeFilterLabel(key) !== targetLabel) {
                    return false;
                }

                return Array.isArray(value) || Array.isArray(value?.data);
            });

            if (directMatch) {
                const [, value] = directMatch;
                return Array.isArray(value) ? value : value.data;
            }

            const nestedMatch = Object.values(data).find(value => {
                if (!value || typeof value !== 'object') {
                    return false;
                }

                const candidateLabels = [
                    value.label,
                    value.key,
                    value.name,
                    value.filter,
                    value.filterLabel,
                    value.type
                ].map(normalizeFilterLabel);

                return candidateLabels.includes(targetLabel) && Array.isArray(value.data);
            });

            if (Array.isArray(nestedMatch?.data)) {
                return nestedMatch.data;
            }
        }

        return [];
    }, [normalizeFilterLabel]);

    const getAvailableOptionsForFilter = useCallback((targetFilterLabel) => {
        if (!userDetails?.users || !userDetails?.filters) {
            return [];
        }

        const targetFilter = userDetails.filters.find(filter => filter.label === targetFilterLabel);
        if (!targetFilter) {
            return [];
        }

        const scopeFilter = userDetails.filters.find(filter => filter.label === 'Scope');
        const selectedScopes = Array.isArray(scopeFilter?.selectedData) ? scopeFilter.selectedData : [];
        const hasBgScope = selectedScopes.includes('BG');
        const hasSiteClusterScope = selectedScopes.includes('Site cluster');
        const hasAnySelectedFilter = userDetails.filters.some(filter =>
            Array.isArray(filter?.selectedData) && filter.selectedData.length > 0
        );

        if (targetFilterLabel === 'Site cluster' && hasBgScope && !hasSiteClusterScope) {
            return [];
        }

        if (targetFilterLabel === 'BG' && hasSiteClusterScope && !hasBgScope) {
            return [];
        }

        const usersMatchingOtherFilters = userDetails.users.filter(user =>
            userDetails.filters
                .filter(filter => filter.label !== targetFilterLabel)
                .every(filter => doesUserMatchFilter(user, filter))
        );

        const availableValues = new Set();

        usersMatchingOtherFilters.forEach(user => {
            const userValue = getUserValueForFilter(user, targetFilterLabel);

            if (Array.isArray(userValue)) {
                userValue.forEach(value => {
                    if (value !== null && value !== undefined && value !== '') {
                        availableValues.add(value);
                    }
                });
                return;
            }

            if (userValue !== null && userValue !== undefined && userValue !== '') {
                availableValues.add(userValue);
            }
        });

        // Keep selected values visible so users can always unselect them.
        (targetFilter.selectedData || []).forEach(selectedValue => {
            if (selectedValue !== null && selectedValue !== undefined && selectedValue !== '') {
                availableValues.add(selectedValue);
            }
        });

        const rawOptions = getFilterRawOptions(targetFilter);

        if (!hasAnySelectedFilter && rawOptions.length > 0) {
            return rawOptions;
        }

        const filteredOriginalOptions = rawOptions.filter(option =>
            availableValues.has(getComparableOptionValue(option))
        );

        if (rawOptions.length > 0) {
            return filteredOriginalOptions;
        }

        const existingOptionValues = new Set(
            filteredOriginalOptions.map(option => getComparableOptionValue(option))
        );

        const additionalOptions = Array.from(availableValues).filter(
            value => !existingOptionValues.has(value)
        );

        return [...filteredOriginalOptions, ...additionalOptions];
    }, [userDetails, doesUserMatchFilter, getUserValueForFilter, getComparableOptionValue, getFilterRawOptions]);

    const favouriteFilters = useMemo(() => {
        return (userDetails?.filters || [])
            .filter(filter => filter.isFavourite)
            .map(filter => ({
                ...filter,
                options: getAvailableOptionsForFilter(filter.label)
            }));
    }, [userDetails?.filters, getAvailableOptionsForFilter]);

    const scopeOptionMeta = useMemo(() => {
        const filters = userDetails?.filters || [];
        const bgFilter = filters.find(filter => filter.label === 'BG');
        const siteClusterFilter = filters.find(filter => filter.label === 'Site cluster');

        return {
            bgOptions: getFilterRawOptions(bgFilter),
            siteClusterOptions: getFilterRawOptions(siteClusterFilter)
        };
    }, [userDetails?.filters, getFilterRawOptions]);

    // Initial data fetch
    useEffect(() => {
        fetchUserDetails();
    }, [fetchUserDetails]);

    const handleMultiSelectChange = useCallback((label, selectedValues) => {
        setUserDetails(prevDetails => ({
            ...prevDetails,
            filters: prevDetails.filters.map(filter =>
                filter.label === label
                    ? { ...filter, selectedData: selectedValues }
                    : filter
            )
        }));
    }, []);

    const resetFilter = useCallback(() => {
        setUserDetails(prevDetails => ({
            ...prevDetails,
            filters: prevDetails.filters.map(filter => ({
                ...filter,
                selectedData: []
            }))
        }));
        showSnackbar('info', 'Filters have been reset');
    }, [showSnackbar]);

    // Popup handlers
    const handleOpenAddUser = useCallback(() => {
        setActionType('add');
        setSelectedUser(null);
        setOpenAddUser(true);
    }, []);

    const handleCloseAddUser = useCallback(() => {
        setOpenAddUser(false);
        setSelectedUser(null);
        setActionType('add');
    }, []);

    const handleEditUserOpen = useCallback((userData) => {
        setActionType('edit');
        setSelectedUser(userData);
        setOpenAddUser(true);
    }, []);

    const handleSaveUser = useCallback((userData, message) => {
        if (actionType === 'add') {
            setUserDetails(prevDetails => ({
                ...prevDetails,
                users: [...(prevDetails.users || []), userData]
            }));
            showSnackbar('success', message || 'User added successfully!');
        } else if (actionType === 'edit') {
            setUserDetails(prevDetails => ({
                ...prevDetails,
                users: prevDetails.users.map(user =>
                    user.ID === userData.ID ? userData : user
                )
            }));
            showSnackbar('success', message || 'User updated successfully!');
        }
        setOpenAddUser(false);
        setSelectedUser(null);
        setActionType('add');
    }, [actionType, showSnackbar]);

    const handleUserSelection = useCallback((selectedUserList) => {
        setSelectedUsers(selectedUserList);
    }, []);

    const handleDeleteUser = useCallback((userNames) => {
        setSelectedUsers(Array.isArray(userNames) ? userNames : [userNames]);
        setBulkDeletePopupOpen(true);
    }, []);


    const handleExportUser = useCallback(async () => {
        try {
            setExportLoader(true);
            const selectedFilters = userDetails?.filters?.map(f => ({
                label: f.label,
                selectedData: f.selectedData
            })) || [];

            await exportToExcelFile(selectedFilters);

            showSnackbar('success', "Export generated successfully!");

        } catch (error) {
            console.log("Error exporting user data:", error);
            showSnackbar("error", "Failed to export.");
        } finally {
            setExportLoader(false);
        }
    }, [userDetails, showSnackbar]);

    const getUserIdsFromNames = useCallback((userNames) => {
        if (!userDetails?.users || !Array.isArray(userDetails.users)) {
            return [];
        }

        const matchedUsers = userDetails.users.filter(user => {
            const isMatch = userNames.includes(user.Name);
            return isMatch;
        });

        const userIds = matchedUsers.map(user => {
            const userId = user.ID || user.id || user.userId || user.user_id;
            return userId;
        });

        // Filter out null/undefined values
        const validUserIds = userIds.filter(id => id !== null && id !== undefined);
        return validUserIds;
    }, [userDetails]);

    const getUserDataFromNames = useCallback((userNames) => {
        if (!userDetails?.users || !Array.isArray(userDetails.users)) {
            return [];
        }

        const userData = userDetails.users.filter(user => {
            const isMatch = userNames.includes(user.Name);
            return isMatch;
        });

        return userData;
    }, [userDetails]);

    const handleDeleteConfirm = useCallback(async () => {
        try {
            const userIds = getUserIdsFromNames(selectedUsers);
            const userData = getUserDataFromNames(selectedUsers);

            if (userIds.length) {
                showSnackbar('error', 'No valid users selected for deletion. Please check user data.');
                return;
            }

            if (userData.length === 0) {
                showSnackbar('error', 'Unable to find user information for deletion');
                return;
            }

            // Check for ID/data mismatch
            if (userIds.length !== selectedUsers.length) {
                showSnackbar('warning', 'Some selected users may not have valid IDs. Proceeding with available users.');
            }

            let adGroupResponse;

            if (userIds.length === 1) {
                const singleUser = userData[0];

                if (!singleUser['Email ID']) {
                    showSnackbar('error', 'Selected user is missing email information');
                    return;
                }

                try {
                    adGroupResponse = await removeUserFromAdGroup(
                        singleUser['Email ID'],
                        instance,
                        activeAccount
                    );

                    if (adGroupResponse != 204 && adGroupResponse != 200) {
                        console.warn('AD group removal returned unexpected status:', adGroupResponse);
                    }

                } catch (adError) {
                    console.error('Error removing user from AD group:', adError);
                    showSnackbar('warning', 'User will be removed from database but may still exist in AD group.');
                }
                await deleteUserAPI(userIds[0]);

            } else {
                const userEmails = userData.map(user => user['Email ID']).filter(Boolean);

                if (userEmails.length !== userData.length) {
                    const missingEmails = userData.filter(user => !user['Email ID']).map(user => user.Name);
                    showSnackbar('warning', `Some users may not be removed from AD group due to missing email: ${missingEmails.join(', ')}`);
                }

                try {
                    adGroupResponse = await removeMultipleUsersFromAdGroup(
                        userEmails,
                        instance,
                        activeAccount
                    );

                    if (adGroupResponse?.failedRemovals?.length > 0) {
                        const failedEmails = adGroupResponse.failedRemovals.join(', ');
                        showSnackbar('warning', `Some users may still exist in AD group: ${failedEmails}`);
                    }

                } catch (adError) {
                    console.error('Error removing multiple users from AD group:', adError);
                    showSnackbar('warning', 'Users will be removed from database but may still exist in AD group.');
                }

                await deleteMultiUserAPI(userIds);
            }

            setUserDetails(prevDetails => ({
                ...prevDetails,
                users: prevDetails.users.filter(user =>
                    !selectedUsers.includes(user.Name)
                )
            }));

            setBulkDeletePopupOpen(false);
            setSelectedUsers([]);

            const userCount = selectedUsers.length;
            const message = userCount === 1
                ? 'User deleted successfully'
                : `${userCount} users deleted successfully`;
            showSnackbar('success', message);
        } catch (error) {
            console.error("Error deleting users:", error);
            let errorMessage;

            if (error.message?.includes('not found in Azure Active Directory')) {
                errorMessage = 'User not found in Azure AD but will be removed from database';

                try {
                    const userIds = getUserIdsFromNames(selectedUsers);
                    if (userIds.length === 1) {
                        await deleteUserAPI(userIds[0]);
                    } else {
                        await deleteMultiUserAPI(userIds);
                    }

                    setUserDetails(prevDetails => ({
                        ...prevDetails,
                        users: prevDetails.users.filter(user =>
                            !selectedUsers.includes(user.Name)
                        )
                    }));

                    setBulkDeletePopupOpen(false);
                    setSelectedUsers([]);
                    showSnackbar('success', 'User removed from database (was not in AD group)');
                    return;

                } catch (dbError) {
                    errorMessage = 'Failed to delete from database: ' + dbError.message;
                }
            } else if (error.message?.includes('Insufficient permissions')) {
                errorMessage = 'Insufficient permissions to remove from AD group. Database deletion cancelled.';
            } else if (error.message?.includes('Failed to fetch user details')) {
                errorMessage = 'Unable to verify user in Azure AD. Please check user email and try again.';
            } else {
                errorMessage = error.response?.data?.message ||
                    error.message ||
                    'Failed to delete users. Please try again.';
            }

            showSnackbar('error', errorMessage);
        }
    }, [selectedUsers, getUserIdsFromNames, getUserDataFromNames, showSnackbar, instance, activeAccount]);

    return (
        <div className={styles.adminContainer}>
            {isLoading ? (
                <LoadingComponent />
            ) : (
                <>
                    {/* Filters Section */}
                    <div className={styles.filters}>
                        {favouriteFilters
                            .map(filter => (
                                <ListItem key={filter.label} className={styles.listCustomClass}>
                                    <MultiSelectWithSearchInput
                                        key={filter.label}
                                        label={filter.label}
                                        options={filter.options}
                                        filterValues={filter.selectedData}
                                        handleFilter={handleMultiSelectChange}
                                        disabled={false}
                                        type={adminType}
                                    />
                                </ListItem>
                            ))}
                        {/* <div style={{ paddingLeft: '5px', display: 'flex', alignItems: 'center' }}> */}
                        <img
                            src={resetBtn}
                            onClick={resetFilter}
                            alt="Reset Filters"
                            style={{ cursor: 'pointer', marginTop: '20px' }}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className={styles.btnContainer}>
                        <CustomButton
                            type="adminAddNew"
                            label=" Add new"
                            icon={plusIcon}
                            onClick={handleOpenAddUser}
                        />
                        <CustomButton
                            type="adminAddNew"
                            label=" Export"
                            icon={Excel}
                            onClick={handleExportUser}
                        />
                        {selectedUsers.length > 1 && (
                            <>
                                <CustomButton
                                    label="Bulk delete"
                                    type="adminAddNew"
                                    icon={adminDeleteIcon}
                                    onClick={() => setBulkDeletePopupOpen(true)}
                                />
                                <CustomButton
                                    label="Cancel"
                                    type="adminCancel"
                                    onClick={() => setSelectedUsers([])}
                                />
                            </>
                        )}
                    </div>

                    {/* Main Content - User Table */}
                    <div className="mainContent">
                        <UserTable
                            data={getFilteredUsers()}
                            scopeOptionMeta={scopeOptionMeta}
                            selectedUsers={selectedUsers}
                            onUserSelection={handleUserSelection}
                            onEditUser={handleEditUserOpen}
                            onDeleteUser={handleDeleteUser}
                        />
                    </div>

                    {/* Add/Edit User Popup */}
                    <AddAndEditPopup
                        open={openAddUser}
                        onClose={handleCloseAddUser}
                        onSubmit={handleSaveUser}
                        type={actionType}
                        selectedUserData={selectedUser}
                        personaMapping={userDetails?.personaScopeMapping}
                        dropdownData={userDetails?.dropdownDetails}
                        showSnackbar={showSnackbar}
                        activeAccount={activeAccount}
                        instance={instance}
                    />

                    {/* Delete Confirmation Popup */}
                    <DeletePopup
                        open={bulkDeletePopupOpen}
                        selectedUsers={selectedUsers}
                        onClose={() => setBulkDeletePopupOpen(false)}
                        onConfirm={handleDeleteConfirm}
                    />

                    {/* SnackBar Component */}
                    <SnackBarMessage
                        type={snackBarObject.type}
                        snackbarMessage={snackBarObject.message}
                        snackbarOpen={snackBarObject.snackbarOpen}
                        setSnackbarOpen={closeSnackbar}
                    />

                    {exportLoader && <ExportLoader type={'User'}/>}
                </>
            )}
        </div>
    );
};

export default User;